"use client";

import { useLocaleStringContext } from "../../helpers/LocaleStringContext";
import { HighInSugarIconCl } from "./icons/HighInSugarIconCl";
import { HighInSugarIconPe } from "./icons/HighInSugarIconPe";
import { VegetarianMarkIn } from "./icons/VegetarianMarkIn";
import classNames from "classnames/bind";
import { ReactNode } from "react";
import styles from "./DietaryMarkList.module.css";

const cx = classNames.bind(styles);

export const DietaryMarkList = ({
  className,
  productIsVegetarian,
  productIsHighSugar,
  children,
}: {
  className?: string;
  productIsVegetarian?: boolean;
  productIsHighSugar?: boolean;
  children?: ReactNode[];
}) => {
  const localeString = useLocaleStringContext();

  const selectShowGreenDot = (localeString: string) =>
    localeString === "in-en" || localeString === "in-hi";

  const selectShowHighInSugarIcon = (localeString: string) =>
    localeString === "pe-es" || localeString === "cl-es";

  /* Does the local use marks? */
  const localeUsesVegetarianMark =
    selectShowGreenDot(localeString) && productIsVegetarian;
  const localeUsesHighInSugarMark =
    selectShowHighInSugarIcon(localeString) && productIsHighSugar;

  /* When styling/debugging it's useful to force all marks to appear */
  // const localeUsesVegetarianMark = true;
  // const localeUsesHighInSugarMark = true;

  /* Early return if local doesn't show dietary marks and no children provided */
  if (
    !localeUsesVegetarianMark &&
    !localeUsesHighInSugarMark &&
    (!children || children.length === 0)
  ) {
    return null;
  }

  /**
   * NOTE: using a div wrapper as it does not have default margins or display that a UL has.
   * Makes it easier to override styles in the parent component.
   */
  return (
    <div className={cx("dietary-marks", className)}>
      <ul className={cx("dietary-marks-list")} role="list">
        {localeUsesVegetarianMark && (
          <>
            {(localeString === "in-en" || localeString === "in-hi") && (
              <li>
                <VegetarianMarkIn className={cx("vegetarian")} />
              </li>
            )}
          </>
        )}
        {localeUsesHighInSugarMark && (
          <>
            {localeString === "cl-es" && (
              <li>
                <HighInSugarIconCl className={cx("high-sugar")} />
              </li>
            )}

            {localeString === "pe-es" && (
              <li>
                <HighInSugarIconPe className={cx("high-sugar")} />
              </li>
            )}
          </>
        )}
        {/* Render additional children if provided on ingredients page */}
        {children &&
          children.map((child, index) => <li key={index}>{child}</li>)}
      </ul>
    </div>
  );
};
